import classNames from 'classnames';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { AttentionMessage, ErrorMessage } from '@eika/attention-message';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { useDebounce } from '@eika/hooks';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';
import { Select } from '@eika/select';

import { CustomSelect } from '../../../components/CustomSelect';
import { AccountLabels } from '../../../components/shared/AccountLabels';
import { formatWithCurrency } from '../../../components/shared/numberFormat';
import { useSuspendedAccounts } from '../../../fetchHooks/useAccount';
import { useAccountNotificationMutations, useAccountNotifications } from '../../../fetchHooks/useAccountNotifications';
import { useSuspendedFavoriteAccount } from '../../../fetchHooks/useFavoriteAccount';
import { Account, AccountNotification, INotificationRule } from '../../../generated-types';
import useTranslation, { format } from '../../../hooks/useTranslation';
import AccountTransactionActiveToggle from './AccountTransactionActiveToggle/AccountTransactionActiveToggle';
import AccountTransactionNotificationSettingsSkeleton from './skeleton/AccountTransactionNotificationSettingsSkeleton';

import './AccountTransactionNotificationSettings.scss';

type AccountTransactionalSettingsOptions = 'BALANCE_LESS_THAN' | 'DEPOSIT_MORE_THAN';

const MAX_AMOUNT = 999_999_999;

const OPTIONS_TO_CREATE_NOTIFICATION: AccountTransactionalSettingsOptions[] = [
    'BALANCE_LESS_THAN',
    'DEPOSIT_MORE_THAN',
];

const AccountTransactionNotificationSettings: React.FC = () => {
    const { cancel, pushNotifications: pushNoticiationTexts } = useTranslation();

    const [selectedAccount, setSelectedAccount] = useState<Account>();
    const [selectedOption, setSelectedOption] = useState<AccountTransactionalSettingsOptions>('BALANCE_LESS_THAN');
    const [amount, setAmount] = useState<number>();
    const [amountError, setAmountError] = useState<string>();

    const { data: accountTransactionalNotifications = [] } = useAccountNotifications();
    const [showCreateNewNotification, setShowCreateNewNotification] = useState(false);
    const [highlightAttentionMessage, setHighlightAttentionMessage] = useState(false);
    const { createAccountNotification, deleteAccountNotification } = useAccountNotificationMutations();

    const { data: accounts } = useSuspendedAccounts({
        select: (accounts) =>
            accounts.filter((account) => account.rights.paymentFrom && !!account.rights.accessPermission),
    });

    const availableAccountsForSelectedOption = useMemo(() => {
        const accountNumbersWithSelectedNotification = accountTransactionalNotifications
            .filter((account) => account.type === selectedOption)
            .map((account) => account.accountNo.value);
        return accounts.filter(
            (account) => !accountNumbersWithSelectedNotification.includes(account.accountNumber.value),
        );
    }, [accountTransactionalNotifications, accounts, selectedOption]);

    const { data: favoriteAccount } = useSuspendedFavoriteAccount();

    useEffect(() => {
        if (
            !selectedAccount ||
            !availableAccountsForSelectedOption.find(
                (account) => account.accountNumber.value === selectedAccount.accountNumber.value,
            )
        ) {
            setSelectedAccount(
                availableAccountsForSelectedOption.find(
                    (account) => account.accountNumber.value === favoriteAccount?.value,
                ) ?? availableAccountsForSelectedOption[0],
            );
        }
    }, [selectedAccount, favoriteAccount, setSelectedAccount, availableAccountsForSelectedOption]);

    const debouncedCreateReset = useDebounce(() => {
        createAccountNotification.reset();
        setSelectedOption('BALANCE_LESS_THAN');
        setHighlightAttentionMessage(false);
        setAmountError(undefined);
        setAmount(0);
    }, 500);

    const onChangeAmount = (number: number | undefined) => {
        setAmountError(undefined);
        setAmount(number);
    };

    const onSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setAmountError(undefined);
            if ((amount ?? 0) > MAX_AMOUNT) {
                setAmountError(
                    format(pushNoticiationTexts.account.amountError, formatWithCurrency(MAX_AMOUNT, { fractals: 0 })),
                );
                return;
            }
            if (availableAccountsForSelectedOption.length === 0) {
                setHighlightAttentionMessage(true);
                return;
            }
            const payload: INotificationRule = {
                accountNo: selectedAccount?.accountNumber ?? accounts[0].accountNumber,
                type: selectedOption,
                amount,
            };

            await createAccountNotification.mutateAsync(payload);

            setShowCreateNewNotification(false);
            debouncedCreateReset();
        },
        [
            createAccountNotification,
            selectedAccount,
            debouncedCreateReset,
            selectedOption,
            accounts,
            amount,
            availableAccountsForSelectedOption,
            pushNoticiationTexts.account.amountError,
        ],
    );

    const debouncedDeleteReset = useDebounce(() => deleteAccountNotification.reset(), 500);

    const removeAccountTransactionalNotification = useCallback(
        async (targetNotification: AccountNotification) => {
            await deleteAccountNotification.mutateAsync(targetNotification.id);
            debouncedDeleteReset();
        },
        [deleteAccountNotification, debouncedDeleteReset],
    );

    const filteredAccountTransactionNotifications = useMemo(
        () =>
            accountTransactionalNotifications.filter(
                (notification) => notification.transactionType !== 'WAGE' && notification.transactionType !== 'PENSION',
            ),
        [accountTransactionalNotifications],
    );

    return (
        <section className="account-transaction-notification-settings">
            <div className="account-transaction-notification-settings__intro-section">
                <span className="semibold">{pushNoticiationTexts.account.customNotifications}</span>
                <ul className="account-transaction-notification-settings__active-toggle-list">
                    {filteredAccountTransactionNotifications.map((accountTransactionNotification) => (
                        <AccountTransactionActiveToggle
                            key={accountTransactionNotification.id.toString()}
                            notification={accountTransactionNotification}
                            targetAccount={accounts.find(
                                (account) =>
                                    account.accountNumber.value === accountTransactionNotification.accountNo.value,
                            )}
                            removeNotification={removeAccountTransactionalNotification}
                            isLoading={deleteAccountNotification.isPending}
                        />
                    ))}
                </ul>
            </div>
            <details
                className="account-transaction-notification-settings__collapsable-section"
                open={showCreateNewNotification}
                onToggle={(e) => setShowCreateNewNotification(e.currentTarget.open)}
            >
                <summary className="account-transaction-notification-settings__edit-intro-section">
                    <span className="bold">{pushNoticiationTexts.account.createNotificationRule}</span>
                    <span
                        className={classNames('account-transaction-notification-settings__edit-wrapper', {
                            'account-transaction-notification-settings__edit-wrapper--open': showCreateNewNotification,
                        })}
                    >
                        <span
                            aria-hidden={showCreateNewNotification}
                            className="account-transaction-notification-settings__cancel-text"
                        >
                            {cancel}
                        </span>
                        <span className="account-transaction-notification-settings__edit-icon-wrapper">
                            <Icon
                                className="icon--24 account-transaction-notification-settings__edit-icon"
                                type={IconTypes.PLUSS_24}
                            />
                        </span>
                    </span>
                </summary>

                <form className="account-transaction-notification-settings__form" onSubmit={onSubmit}>
                    <Select
                        label={pushNoticiationTexts.account.chooseNotificationType}
                        value={selectedOption}
                        onChange={(e) =>
                            setSelectedOption(e.currentTarget.value as AccountTransactionalSettingsOptions)
                        }
                        values={OPTIONS_TO_CREATE_NOTIFICATION.map((option) => ({
                            value: option,
                            text: pushNoticiationTexts.accountSpecificNotifications[option].title,
                            key: option,
                        }))}
                    />
                    <CustomSelect.Root
                        label={pushNoticiationTexts.account.chooseAccount}
                        id="transaction-notification-account"
                        onValueChange={(accountNumber) =>
                            setSelectedAccount(
                                accounts.find((account) => account.accountNumber.value === accountNumber),
                            )
                        }
                        value={selectedAccount?.accountNumber.value}
                    >
                        <CustomSelect.Button
                            placeholder={pushNoticiationTexts.chooseAccountHere}
                            locked={accounts.length <= 1 || createAccountNotification.isPending}
                        >
                            {availableAccountsForSelectedOption.length === 0 ? (
                                <span className="account-transaction-notification-settings__form-select-result">
                                    <span className="from-account-select__list-item-owners" />
                                    <span className="semibold">
                                        {pushNoticiationTexts.account.noAccountsToChooseFrom}
                                    </span>
                                </span>
                            ) : (
                                <span className="account-transaction-notification-settings__form-select-result">
                                    <span className="from-account-select__list-item-owners">
                                        <AccountLabels
                                            owners={selectedAccount?.owners ?? []}
                                            className="account-button__owner-label"
                                        />
                                    </span>
                                    <span className="semibold">{selectedAccount?.alias}</span>
                                    <span>{formatAccountNumber(selectedAccount?.accountNumber)}</span>
                                </span>
                            )}
                        </CustomSelect.Button>
                        <CustomSelect.OptionList>
                            {availableAccountsForSelectedOption.map((account) => (
                                <CustomSelect.Option key={account.accountId.value} item={account.accountNumber.value}>
                                    <span className="account-transaction-notification-settings__form-select-result">
                                        <span className="from-account-select__list-item-owners">
                                            <AccountLabels
                                                owners={account.owners ?? []}
                                                className="account-button__owner-label"
                                            />
                                        </span>
                                        <span className="semibold">{account.alias}</span>
                                        <span>{formatAccountNumber(selectedAccount?.accountNumber)}</span>
                                    </span>
                                </CustomSelect.Option>
                            ))}
                        </CustomSelect.OptionList>
                    </CustomSelect.Root>
                    {availableAccountsForSelectedOption.length === 0 && (
                        <AttentionMessage
                            type={highlightAttentionMessage ? 'error' : 'default'}
                            icon={IconTypes.INFO_24}
                            text={pushNoticiationTexts.account.youHaveNoMoreAccountsToAddThisNotificationTo}
                        />
                    )}
                    <CurrencyInput
                        name="amount"
                        label={pushNoticiationTexts.account.chooseAmount}
                        onValueChange={(e) => onChangeAmount(e.floatValue)}
                        value={amount ? formatCurrencyValue(amount) : undefined}
                        error={amountError}
                        max={MAX_AMOUNT}
                    />
                    {createAccountNotification.error && (
                        <ErrorLabel
                            className="account-transaction-notification-settings__form-error"
                            icon={IconTypes.UTROPSTEGN_24}
                            text={pushNoticiationTexts.account.errorCreatingNotification}
                        />
                    )}
                    <ButtonWithSpinner
                        className="account-transaction-notification-settings__form-submit"
                        showSpinner={createAccountNotification.isPending}
                        showSuccess={createAccountNotification.isSuccess}
                        type="submit"
                    >
                        {pushNoticiationTexts.account.createNotification}
                    </ButtonWithSpinner>
                </form>
            </details>
        </section>
    );
};

export default () => {
    const { pushNotifications: pushNotificationTexts } = useTranslation();
    return (
        <ErrorBoundary
            fallback={<ErrorMessage text={pushNotificationTexts.account.couldNotGetNotificationsForAccount} />}
        >
            <Suspense fallback={<AccountTransactionNotificationSettingsSkeleton />}>
                <AccountTransactionNotificationSettings />
            </Suspense>
        </ErrorBoundary>
    );
};
